import styled, { css } from 'styled-components';
import { ReactNode } from 'react';

interface GlassCardProps {
  children: ReactNode;
  variant?: 'default' | 'elevated' | 'subtle';
  hover?: boolean;
  padding?: 'none' | 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  onClick?: () => void;
  as?: keyof JSX.IntrinsicElements;
  accentColor?: string;
}

const paddingMap = {
  none: '0',
  sm: 'var(--space-3)',
  md: 'var(--space-4)',
  lg: 'var(--space-6)',
  xl: 'var(--space-8)',
};

const StyledCard = styled.div<{
  $variant: 'default' | 'elevated' | 'subtle';
  $hover: boolean;
  $padding: string;
  $clickable: boolean;
  $accentColor?: string;
}>`
  position: relative;
  padding: ${props => props.$padding};
  border-radius: var(--radius-xl);
  transition: transform var(--transition-base), box-shadow var(--transition-base);

  ${props => props.$variant === 'default' && css`
    background: var(--glass-background);
    backdrop-filter: var(--glass-blur);
    -webkit-backdrop-filter: var(--glass-blur);
    border: 1px solid var(--glass-border);
    box-shadow: var(--shadow-card);
  `}

  ${props => props.$variant === 'elevated' && css`
    background: var(--glass-background-elevated);
    backdrop-filter: var(--glass-blur);
    -webkit-backdrop-filter: var(--glass-blur);
    border: 1px solid var(--glass-border-strong);
    box-shadow: var(--shadow-lg);
  `}

  ${props => props.$variant === 'subtle' && css`
    background: var(--color-background-secondary);
    border: 1px solid var(--color-border);
    box-shadow: var(--shadow-sm);
  `}

  ${props => props.$hover && css`
    &:hover {
      transform: translateY(-4px);
      box-shadow: var(--shadow-card-hover);
    }
  `}

  ${props => props.$clickable && css`
    cursor: pointer;
  `}

  ${props => props.$accentColor && css`
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 3px;
      background: ${props.$accentColor};
      border-radius: var(--radius-xl) var(--radius-xl) 0 0;
    }
  `}

  /* Inset highlight */
  &::after {
    content: '';
    position: absolute;
    top: 1px;
    left: 1px;
    right: 1px;
    height: 1px;
    background: linear-gradient(90deg,
      transparent,
      rgba(255, 255, 255, 0.5) 20%,
      rgba(255, 255, 255, 0.5) 80%,
      transparent
    );
    border-radius: var(--radius-xl) var(--radius-xl) 0 0;
    pointer-events: none;
  }
`;

export function GlassCard({
  children,
  variant = 'default',
  hover = false,
  padding = 'lg',
  className,
  onClick,
  as,
  accentColor,
}: GlassCardProps) {
  return (
    <StyledCard
      as={as}
      className={className}
      $variant={variant}
      $hover={hover}
      $padding={paddingMap[padding]}
      $clickable={!!onClick}
      $accentColor={accentColor}
      onClick={onClick}
    >
      {children}
    </StyledCard>
  );
}
